import {
  Select,
  SelectVariant,
  makeStyles,
  textStyleUtils
} from '@focaldata/cin-ui-components'
import { getLanguageLabel } from '../../utils/HelperFunctions'

interface StyleProps {
  padding: string
}

const useStyles = makeStyles<{ styleProps: StyleProps }>()(
  (_theme, { styleProps }) => ({
    wrapper: {
      display: 'flex',
      alignItems: 'center',
      padding: styleProps.padding
    }
  })
)

interface Props {
  /** Language codes to offer, in display order. */
  languages: string[]
  selectedLanguage: string | undefined
  onLanguageChange: (language: string) => void
  /** Distinguishes the surfaces for assistive tech and for tests. */
  ariaLabel: string
  /** Wrapper padding, so each surface lines up with the row above it. */
  padding: string
}

/**
 * A "Language" label plus an underlined select of language codes, shown with
 * human-readable names.
 *
 * Shared by the audience builder and the results pages, which differ only in
 * where their languages come from and how the selection is stored. Renders
 * nothing for a single language — there is nothing to choose, and both surfaces
 * apply that one language silently. Callers with a stricter notion of
 * "multi-language" (the audience builder counts allocations, not selectable
 * codes) guard before rendering.
 */
export const LanguageSelect = ({
  languages,
  selectedLanguage,
  onLanguageChange,
  ariaLabel,
  padding
}: Props) => {
  const { classes } = useStyles({ styleProps: { padding } })
  const { classes: textClasses, cx } = textStyleUtils.useTextStyles()

  if (languages.length <= 1) return null

  const options = languages.map((code) => ({
    id: code,
    name: getLanguageLabel(code),
    value: code
  }))

  return (
    <div className={classes.wrapper}>
      <p
        className={cx(
          textClasses.default,
          textClasses.marginRight,
          textClasses.sizeMs
        )}
      >
        Language
      </p>
      <Select
        variant={SelectVariant.Underlined}
        options={options}
        // Falls back to the first option so the control's value always matches
        // one: nothing picked yet still has to render as a valid selection.
        optionValue={selectedLanguage ?? languages[0]}
        ariaLabel={ariaLabel}
        onChange={(e) => onLanguageChange(e.target.value as string)}
      />
    </div>
  )
}

export default LanguageSelect
